'use client'
import { type OptionSelect, Select } from '@morpho-org/ui-v6/client'
import { Card } from '@morpho-org/ui-v6/server'
import { formatters } from '@morpho-org/ui-v6/utils'
import { SegmentedControlItem, SegmentedControlList, Tabs } from '@repo/ui'
import { useState } from 'react'

import SimpleChart from '@/components/charts/components/SimpleChart'
import { formatTimeseriesData } from '@/components/charts/helpers/highcharts'
import { useHighchartsOptions } from '@/components/charts/hooks/useHighchartsOptions'
import { useIsMobile } from '@/hooks/useBreakpoints'

import type { MarketInstantRates } from './instantRates.contract'

const percentageFormatter = formatters.percent.digits(2).default('-')

type Mode = 'borrowApy' | 'supplyApy' | 'apyAtTarget'

const modeOptions: OptionSelect<Mode>[] = [
  {
    value: 'borrowApy',
    label: 'Borrow'
  },
  {
    value: 'supplyApy',
    label: 'Lend'
  },
  {
    value: 'apyAtTarget',
    label: 'Rate at target'
  }
]

const modeTitles: Record<Mode, string> = {
  borrowApy: 'Instantaneous Rate',
  supplyApy: 'Instantaneous Lend Rate',
  apyAtTarget: 'Instantaneous Rate At Target'
} as const

export interface ChartInstantApyClientProps {
  rates: MarketInstantRates
}

const ChartInstantApyClient = ({ rates }: ChartInstantApyClientProps) => {
  const isMobile = useIsMobile()
  const { palette } = useHighchartsOptions()

  const [mode, setMode] = useState<Mode>('borrowApy')

  const formattedData = formatTimeseriesData(
    rates.points.flatMap(point => {
      const y = point[mode]
      return y === null ? [] : [{ x: point.timestamp, y }]
    })
  )

  const value = rates[mode]

  return (
    <Card className="gap-sm px-md md:px-lg py-lg w-full flex-col" withBorder>
      <div className="gap-sm flex flex-wrap items-center justify-between">
        <div className="gap-sm md:gap-xs flex flex-col">
          <div className="flex items-center gap-[2px]">
            <span className="text-secondary-foreground text-xs">{modeTitles[mode]}</span>
          </div>
          {isMobile && (
            <div className="gap-sm flex flex-wrap items-center">
              <Select<Mode> options={modeOptions} value={mode} onChange={setMode} />
            </div>
          )}
          <div className="flex items-center gap-[2px]">
            <span className="text-title-sm">{percentageFormatter.of(value)}</span>
            <span className="text-title-sm text-muted-foreground">%</span>
          </div>
        </div>

        {!isMobile && (
          <div className="gap-sm flex flex-wrap items-center">
            <Tabs value={mode} onValueChange={value => setMode(value as Mode)}>
              <SegmentedControlList size="default" aria-label="Rate">
                {modeOptions.map(option => (
                  <SegmentedControlItem key={option.value} value={option.value}>
                    {option.label}
                  </SegmentedControlItem>
                ))}
              </SegmentedControlList>
            </Tabs>
          </div>
        )}
      </div>
      <SimpleChart
        statuses={{ loading: false }}
        unit="%"
        height={isMobile ? '230px' : '224px'}
        series={[
          {
            type: 'areaspline',
            name: `Total ${mode}`,
            data: formattedData,
            color: palette.colors[0],
            lineColor: palette.colors[0],
            fillColor: palette.areaChartColors[0]
          }
        ]}
        additionalOptions={{
          yAxis: {
            softMin: 0,
            maxPadding: 0.4
          },
          chart: {
            plotBackgroundColor: 'var(--muted)'
          }
        }}
      />
    </Card>
  )
}

export default ChartInstantApyClient
